import React from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner, theme, css, flexCenter, useTranslationTyped } from '@kantera/ui';
import { useIsSelfPlayer } from '../hooks/useIsSelfPlayer';
import { EmptyState } from './EmptyState';
import { PageHeader } from './PageHeader';

/**
 * Route guard for the KI-010 player-self portal screens (§8.1 – §8.6).
 *
 * Three-tier fail-closed gate, mirroring the API guard
 * (`resolvePortalPlayerOrSendError`) so the UI never tries to fetch data the
 * backend would 403 anyway:
 *
 *  1. Not authenticated         → /login
 *  2. Authenticated but not a player → /        (their normal dashboard)
 *  3. Authenticated player, server says 13+ → the page. Nothing else admits.
 *
 * Everyone else is NOT admitted, and is told why (KAN-63). This used to send
 * every one of them to /family-circle, which is parent-only, so a child bounced
 * on to '/' with no explanation. `isThirteenPlus` is false for several different
 * callers, and each gets its own outcome:
 *
 *  a. The server positively says this is the caller's OWN record and it is under
 *     13 → the under-13 message in place. A parent manages an under-13 account
 *     (2026-09-01 ruling) and no child-facing page exists to send them to.
 *  b. The account has the parent role (e.g. a parent who also coaches and plays,
 *     whose record resolves to their child) → /family-circle, as before. A parent
 *     can go there. A child's own login ('player' with no admin or coach role)
 *     never has it here, even when its token lists 'parent' (KAN-143,
 *     useIsSelfPlayer): /family-circle would turn it away to '/', so it gets (c).
 *  c. Anything else — the answer is unknown (probe 500 / network), the player
 *     role has no linked record, or the record is someone else's and there is no
 *     parent role to reach /family-circle with → a neutral "unavailable" message
 *     in place. Telling these callers "your parent manages your account" would be
 *     false, and a missing record is the club admin's to fix.
 *
 * The blocked screens show a heading and fixed copy only: no player data, no
 * fetch. `data-portal-gate` lets the real-click crawler score them, since the
 * pathname no longer changes.
 *
 * Defence-in-depth: if either the JWT or the API guard regressed, the other
 * still blocks. Both must fail before a U12 sees portal data.
 */
interface PlayerSelfRouteProps {
  children: React.ReactNode;
}

const PortalBlocked: React.FC<{
  gate: 'under-13' | 'unavailable';
  heading: string;
  title: string;
  description: string;
}> = ({ gate, heading, title, description }) => (
  <div data-portal-gate={gate} style={{ padding: `${theme.spacing['2xl']} ${theme.spacing.xl}` }}>
    <PageHeader title={heading} cinematic={false} />
    <EmptyState title={title} description={description} fullHeight />
  </div>
);

export const PlayerSelfRoute: React.FC<PlayerSelfRouteProps> = ({ children }) => {
  const {
    isLoading,
    isAuthenticated,
    hasPlayerRole,
    hasParentRole,
    accessKnown,
    hasPlayerRecord,
    isSelfPlayer,
    isThirteenPlus,
  } = useIsSelfPlayer();
  const { t } = useTranslationTyped();

  if (isLoading) {
    return (
      <div style={css(flexCenter(), { minHeight: '100vh', background: theme.colors.bg.primary })}>
        <Spinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPlayerRole) {
    return <Navigate to="/" replace />;
  }

  if (!isThirteenPlus) {
    const heading = t('webSidebar.sectionPlayerPortal');

    if (accessKnown && hasPlayerRecord && isSelfPlayer) {
      return (
        <PortalBlocked
          gate="under-13"
          heading={heading}
          title={t('webPlayerPortal.managedByParentTitle')}
          description={t('webPlayerPortal.managedByParentBody')}
        />
      );
    }

    if (hasParentRole) {
      return <Navigate to="/family-circle" replace />;
    }

    // Each key is its own literal t('…') call so the i18n scanners can see it.
    const noRecord = accessKnown && !hasPlayerRecord;
    return (
      <PortalBlocked
        gate="unavailable"
        heading={heading}
        title={
          noRecord ? t('webPlayerPortal.noPlayerProfile') : t('webPlayerPortal.unavailableTitle')
        }
        description={
          noRecord ? t('webPlayerPortal.noPlayerProfileBody') : t('webPlayerPortal.unavailableBody')
        }
      />
    );
  }

  return <>{children}</>;
};
